/**
 * @description
 * Real-time sync status indicator component.
 * Shows last sync time, sync statistics, and refresh button.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 12, 2025
 * @see admin/src/frontend/pages/Settings.tsx
 */

import React, { useState, useEffect } from 'react';
import { RefreshCw, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import type { SyncStatus } from './types';

interface SyncStatusIndicatorProps {
  onRefresh?: () => void;
}

/**
 * Component for displaying current sync status
 */
export const SyncStatusIndicator: React.FC<SyncStatusIndicatorProps> = ({
  onRefresh,
}) => {
  const [status, setStatus] = useState<SyncStatus | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStatus();
  }, []);

  /**
   * Fetch current sync status from API
   */
  const fetchStatus = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/platform-sync/status', {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch sync status');
      }

      const data = (await response.json()) as SyncStatus;
      setStatus(data);
    } catch (error) {
      console.error('Failed to fetch sync status:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle refresh button click
   */
  const handleRefresh = () => {
    fetchStatus();
    if (onRefresh) {
      onRefresh();
    }
  };

  /**
   * Format relative time
   */
  const formatRelativeTime = (timestamp?: string) => {
    if (!timestamp) return 'Never';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="h-5 w-5 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (!status) {
    return (
      <div className="text-center py-4 text-muted-foreground">
        <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">Unable to load sync status</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Status Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Sync Status</h3>
        <button
          onClick={handleRefresh}
          className="text-sm text-primary hover:underline flex items-center gap-1"
          disabled={loading}
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Configuration Status */}
      <div className="p-4 rounded-lg border border-border bg-card">
        <div className="flex items-center gap-2 mb-2">
          {status.isConfigured ? (
            <CheckCircle className="h-5 w-5 text-green-600" />
          ) : (
            <AlertCircle className="h-5 w-5 text-orange-600" />
          )}
          <span className="font-medium text-foreground">
            {status.isConfigured ? 'Platform Connected' : 'Platform Not Connected'}
          </span>
        </div>
        {!status.isConfigured && (
          <p className="text-sm text-muted-foreground">
            Configure platform connection in settings to enable sync
          </p>
        )}
      </div>

      {/* Last Sync */}
      {status.isConfigured && (
        <div className="p-4 rounded-lg border border-border bg-card">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="h-5 w-5 text-muted-foreground" />
            <span className="font-medium text-foreground">Last Sync</span>
          </div>
          <p className="text-sm text-muted-foreground">
            {formatRelativeTime(status.lastSyncAt)}
          </p>
        </div>
      )}

      {/* Statistics */}
      {status.isConfigured && (
        <div className="grid grid-cols-3 gap-3">
          <div className="p-3 rounded-lg border border-border bg-card text-center">
            <div className="text-2xl font-bold text-green-600">
              {status.totalSynced}
            </div>
            <div className="text-xs text-muted-foreground mt-1">Synced</div>
          </div>
          <div className="p-3 rounded-lg border border-border bg-card text-center">
            <div className="text-2xl font-bold text-blue-600">
              {status.totalPending}
            </div>
            <div className="text-xs text-muted-foreground mt-1">Pending</div>
          </div>
          <div className="p-3 rounded-lg border border-border bg-card text-center">
            <div className="text-2xl font-bold text-red-600">
              {status.totalErrors}
            </div>
            <div className="text-xs text-muted-foreground mt-1">Errors</div>
          </div>
        </div>
      )}
    </div>
  );
};

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */